'use client';
import React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cn } from '@/app/lib/utils';
import { Button } from './Button';
import Icon from './Icon';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed z-[100] flex max-w-full md:w-[400px] flex-col top-4 right-4 gap-4',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastMainStyle = cn(
  'group pointer-events-auto relative flex w-[320px] md:w-[400px] items-center bg-background justify-between overflow-hidden rounded-lg transition-all',
  'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full data-[state=open]:sm:slide-in-from-right-full',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full',
  'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
  'data-[swipe=cancel]:translate-x-0',
  'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:animate-out'
);

const ToastCore = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>
>(({ className, ...props }, ref) => {
  return <ToastPrimitives.Root ref={ref} className={cn(toastMainStyle, className)} {...props} />;
});
ToastCore.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action ref={ref} className={className} {...props} />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ ...props }, ref) => (
  <ToastPrimitives.Close ref={ref} {...props} asChild={true}>
    <Button className="p-0 w-4 h-4 absolute top-2 right-2" variant="ghost">
      <Icon name="cross-fill" className="w-4 h-4 text-muted-foreground" />
    </Button>
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  { toastType?: string } & React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, toastType, ...props }, ref) => {
  let titleClass = 'text-zinc-900';
  if (toastType === 'success') {
    titleClass = 'text-green-500';
  }
  if (toastType === 'error') {
    titleClass = 'text-red-500';
  }
  if (toastType === 'warning') {
    titleClass = 'text-yellow-500';
  }

  return (
    <ToastPrimitives.Title
      ref={ref}
      className={cn('text-sm text-foreground font-normal leading-4 pr-2', className, titleClass)}
      {...props}
    />
  );
});
ToastTitle.displayName = ToastPrimitives.Title.displayName;

ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn('text-sm pr-2', className)} {...props} />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof ToastCore>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

const Toast = {
  Provider: ToastProvider,
  Viewport: ToastViewport,
  Core: ToastCore,
  Action: ToastAction,
  Close: ToastClose,
  Title: (props: { toastType?: string } & React.ComponentPropsWithoutRef<typeof ToastTitle>) => (
    <ToastTitle {...props} />
  ),
  Description: ToastDescription,
};

export type { ToastProps, ToastActionElement };

export default Toast;
